/**
 * Pyodide WebAssembly Python Runner Service
 * Runs Python code 100% inside the user's browser sandbox without any VPS or backend compute load.
 */

declare global {
    interface Window {
        loadPyodide: (config?: any) => Promise<any>;
        pyodideInstance?: any;
    }
}

export interface CellExecutionOutput {
    stdout: string;
    stderr: string;
    images: string[]; // Base64 encoded PNGs for matplotlib plots
    htmlResult?: string; // HTML representations (e.g., Pandas DataFrames)
    textResult?: string; // String representations of return values
    error?: string;
    executionTimeMs: number;
}

const PYODIDE_CDN_VERSION = '0.26.4';
const PYODIDE_JS_URL = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_CDN_VERSION}/full/pyodide.js`;
const PYODIDE_INDEX_URL = `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_CDN_VERSION}/full/`;

let pyodidePromise: Promise<any> | null = null;

/**
 * Ensures the Pyodide JavaScript runtime script is injected and loaded into the browser document.
 */
function loadPyodideScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof window.loadPyodide === 'function') {
            resolve();
            return;
        }

        const existingScript = document.getElementById('pyodide-wasm-script');
        if (existingScript) {
            existingScript.addEventListener('load', () => resolve());
            existingScript.addEventListener('error', (e) => reject(e));
            return;
        }

        const script = document.createElement('script');
        script.id = 'pyodide-wasm-script';
        script.src = PYODIDE_JS_URL;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = (err) => reject(new Error(`Failed to load Pyodide from CDN: ${err}`));
        document.head.appendChild(script);
    });
}

/**
 * Initializes or retrieves the singleton Pyodide instance.
 */
export async function getPyodideInstance(onStatusUpdate?: (status: string) => void): Promise<any> {
    if (window.pyodideInstance) {
        return window.pyodideInstance;
    }

    if (pyodidePromise) {
        return pyodidePromise;
    }

    pyodidePromise = (async () => {
        try {
            onStatusUpdate?.('Loading WebAssembly Python engine...');
            await loadPyodideScript();

            onStatusUpdate?.('Initializing Python 3.12 sandbox...');
            const pyodide = await window.loadPyodide({
                indexURL: PYODIDE_INDEX_URL,
            });

            onStatusUpdate?.('Setting up execution environment...');
            // Pre-load common Python libraries for speed
            await pyodide.loadPackage(['micropip']);

            // Setup Python wrapper for capturing stdout, stderr, matplotlib plots, and expressions
            await pyodide.runPythonAsync(`
import sys
import io
import traceback
import ast

# Global persistent storage for execution environment
__COLAB_GLOBALS__ = {}

def __run_colab_cell__(code_str):
    import sys, io, traceback, ast
    
    stdout_capture = io.StringIO()
    stderr_capture = io.StringIO()
    old_stdout, old_stderr = sys.stdout, sys.stderr
    sys.stdout, sys.stderr = stdout_capture, stderr_capture
    
    images = []
    error_msg = None
    html_result = None
    text_result = None
    
    try:
        # Pre-execution check: close prior plot artifacts if matplotlib is loaded
        if 'matplotlib.pyplot' in sys.modules:
            import matplotlib.pyplot as _plt
            _plt.close('all')
            
        parsed = ast.parse(code_str)
        if parsed.body and isinstance(parsed.body[-1], ast.Expr):
            last_expr = parsed.body.pop()
            if parsed.body:
                exec(compile(parsed, '<cell>', 'exec'), __COLAB_GLOBALS__)
            eval_res = eval(compile(ast.Expression(last_expr.value), '<cell>', 'eval'), __COLAB_GLOBALS__)
            if eval_res is not None:
                if hasattr(eval_res, '_repr_html_'):
                    html_result = eval_res._repr_html_()
                else:
                    text_result = repr(eval_res)
        else:
            exec(compile(code_str, '<cell>', 'exec'), __COLAB_GLOBALS__)
            
        # Capture matplotlib plots if any were drawn
        if 'matplotlib.pyplot' in sys.modules:
            import matplotlib.pyplot as _plt
            import base64
            for fig_num in _plt.get_fignums():
                fig = _plt.figure(fig_num)
                buf = io.BytesIO()
                fig.savefig(buf, format='png', bbox_inches='tight', dpi=130, facecolor=fig.get_facecolor(), edgecolor='none')
                buf.seek(0)
                img_b64 = base64.b64encode(buf.read()).decode('utf-8')
                images.append(img_b64)
            _plt.close('all')
            
    except Exception:
        error_msg = traceback.format_exc()
    finally:
        sys.stdout, sys.stderr = old_stdout, old_stderr
        
    return {
        'stdout': stdout_capture.getvalue(),
        'stderr': stderr_capture.getvalue(),
        'images': images,
        'html': html_result,
        'text': text_result,
        'error': error_msg
    }
`);

            window.pyodideInstance = pyodide;
            onStatusUpdate?.('Ready');
            return pyodide;
        } catch (err) {
            pyodidePromise = null;
            throw err;
        }
    })();

    return pyodidePromise;
}

/**
 * Execute Python code cell inside the browser's Pyodide sandbox.
 */
export async function executePythonCell(
    code: string,
    onStatusUpdate?: (status: string) => void
): Promise<CellExecutionOutput> {
    const startTime = performance.now();
    const pyodide = await getPyodideInstance(onStatusUpdate);

    try {
        // Auto-detect and download imported packages on demand (e.g. numpy, pandas, matplotlib, scipy)
        onStatusUpdate?.('Checking dependencies...');
        await pyodide.loadPackagesFromImports(code, {
            messageCallback: (msg: string) => onStatusUpdate?.(`Downloading package: ${msg}`),
        });

        onStatusUpdate?.('Executing...');
        // Pass code safely to the Python cell runner
        pyodide.globals.set('__current_code__', code);
        const resultProxy = await pyodide.runPythonAsync(`__run_colab_cell__(__current_code__)`);
        const result = resultProxy.toJs({ dict_converter: Object.fromEntries });
        resultProxy.destroy();

        const endTime = performance.now();
        const duration = Math.round(endTime - startTime);

        return {
            stdout: result.stdout || '',
            stderr: result.stderr || '',
            images: Array.isArray(result.images) ? result.images : [],
            htmlResult: result.html || undefined,
            textResult: result.text || undefined,
            error: result.error || undefined,
            executionTimeMs: duration,
        };
    } catch (err: any) {
        const endTime = performance.now();
        return {
            stdout: '',
            stderr: '',
            images: [],
            error: err?.message || String(err),
            executionTimeMs: Math.round(endTime - startTime),
        };
    }
}

/**
 * Resets the Python execution environment (clears all global variables and resets namespace).
 */
export async function resetPythonRuntime(): Promise<void> {
    if (!window.pyodideInstance) return;
    await window.pyodideInstance.runPythonAsync(`
__COLAB_GLOBALS__.clear()
if 'matplotlib.pyplot' in sys.modules:
    import matplotlib.pyplot as _plt
    _plt.close('all')
`);
}

/**
 * Writes an uploaded dataset file directly into Pyodide's in-memory Virtual File System (Emscripten FS).
 * This enables students to run `pd.read_csv('filename.csv')` directly in their code.
 */
export async function writeVirtualDatasetFile(
    filename: string,
    data: ArrayBuffer | Uint8Array | string
): Promise<void> {
    const pyodide = await getPyodideInstance();
    const cleanFilename = filename.split(/[\\/]/).pop() || filename;
    
    try {
        const uint8 = typeof data === 'string'
            ? new TextEncoder().encode(data)
            : data instanceof Uint8Array
            ? data
            : new Uint8Array(data);

        // 1. Write to root /
        try {
            pyodide.FS.writeFile(`/${cleanFilename}`, uint8);
        } catch (e) {
            // ignore
        }

        // 2. Write to default Python working directory /home/pyodide/
        try {
            if (!pyodide.FS.analyzePath('/home/pyodide').exists) {
                pyodide.FS.mkdirTree('/home/pyodide');
            }
            pyodide.FS.writeFile(`/home/pyodide/${cleanFilename}`, uint8);
        } catch (e) {
            // ignore
        }

        // 3. Write to current relative working directory
        try {
            pyodide.FS.writeFile(cleanFilename, uint8);
        } catch (e) {
            // ignore
        }

        console.log(`[Pyodide Virtual FS] Successfully mounted dataset: ${cleanFilename} (${uint8.byteLength} bytes)`);
    } catch (err) {
        console.error(`Failed to write file ${cleanFilename} into Pyodide FS:`, err);
        throw err;
    }
}

export interface AssertionTestResult {
    id?: string;
    name: string;
    status: 'PASSED' | 'FAILED';
    points: number;
    earned_points: number;
    error_message?: string;
    explanation?: string;
    is_hidden?: boolean;
}

export interface NotebookEvaluationSummary {
    total_points: number;
    earned_points: number;
    score_percentage: number;
    passed_count: number;
    total_count: number;
    is_passed: boolean;
    results: AssertionTestResult[];
}

/**
 * Evaluates test case assertions against the active Python runtime namespace.
 */
export async function evaluateNotebookAssertions(
    assertions: { id?: string; name: string; assertion_code: string; points?: number; is_hidden?: boolean; explanation?: string }[]
): Promise<NotebookEvaluationSummary> {
    const pyodide = await getPyodideInstance();
    
    const results: AssertionTestResult[] = [];
    let totalPoints = 0;
    let earnedPoints = 0;
    let passedCount = 0;

    for (const item of assertions) {
        const points = item.points ?? 10;
        totalPoints += points;

        try {
            // Run assertion inside the __COLAB_GLOBALS__ namespace
            pyodide.globals.set('__test_code__', item.assertion_code);
            await pyodide.runPythonAsync(`
try:
    exec(__test_code__, __COLAB_GLOBALS__)
    __assertion_status__ = 'PASSED'
    __assertion_err__ = None
except AssertionError as e:
    __assertion_status__ = 'FAILED'
    __assertion_err__ = str(e) if str(e) else 'Assertion check failed.'
except Exception as e:
    __assertion_status__ = 'FAILED'
    __assertion_err__ = f"{type(e).__name__}: {str(e)}"
`);

            const status = pyodide.globals.get('__assertion_status__');
            const err = pyodide.globals.get('__assertion_err__');

            if (status === 'PASSED') {
                earnedPoints += points;
                passedCount++;
                results.push({
                    id: item.id,
                    name: item.name,
                    status: 'PASSED',
                    points,
                    earned_points: points,
                    explanation: item.explanation,
                    is_hidden: item.is_hidden,
                });
            } else {
                results.push({
                    id: item.id,
                    name: item.name,
                    status: 'FAILED',
                    points,
                    earned_points: 0,
                    error_message: err || 'Assertion failed',
                    explanation: item.explanation,
                    is_hidden: item.is_hidden,
                });
            }
        } catch (execErr: any) {
            results.push({
                id: item.id,
                name: item.name,
                status: 'FAILED',
                points,
                earned_points: 0,
                error_message: execErr?.message || String(execErr),
                explanation: item.explanation,
                is_hidden: item.is_hidden,
            });
        }
    }

    const scorePercentage = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 100;

    return {
        total_points: totalPoints,
        earned_points: earnedPoints,
        score_percentage: scorePercentage,
        passed_count: passedCount,
        total_count: assertions.length,
        is_passed: scorePercentage >= 80,
        results,
    };
}

